import React from 'react';
import { IconType } from 'react-icons';

export type ButtonVariant = 'primary' | 'secondary' | 'terciary' | 'outline' | 'outline-orange' | 'outline-green' | 'outline-dark' | 'outline-black-green' | 'ghost' | 'none';

const variantClasses: Record<ButtonVariant, string> = {
    primary: 'bg-brand-orange-20 text-primary-100 border border-brand-orange-20 enabled:hover:shadow-custom-dual enabled:hover:opacity-90 transition-all duration-200',
    secondary: 'bg-brand-green-15 text-primary-100 border border-brand-green-15',
    terciary: 'bg-primary-40 text-primary-100 enabled:hover:shadow-custom-dual enabled:hover:opacity-80 transition-all duration-200 ',
    outline: 'text-system-light-100 border border-system-light-100 enabled:hover:bg-brand-green-5 enabled:hover:shadow-custom-dual hover:brightness-95  transition-all duration-200',
    'outline-orange': 'text-brand-orange-20 border border-brand-orange-20/80 enabled:hover:bg-brand-baige-15 transition-all duration-200',
    'outline-green': 'border border-brand-green-15/20 text-brand-green-15 enabled:hover:shadow-custom-dual enabled:hover:bg-brand-green-5 transition-all duration-200',
    'outline-dark': 'border border-primary-0/60 text-system-light-100 enabled:hover:shadow-custom-dual enabled:hover:bg-system-light-15/20 transition-all duration-200',
    'outline-black-green': 'border border-primary-0/80 bg-brand-green-5 text-primary-40',
    ghost: 'text-brand-green-15 enabled:hover:bg-brand-green-5 transition-all duration-200',
    none: 'transition-all duration-200 enabled:hover:brightness-110'
};

interface ButtonProps {
    onClick?: () => void;
    type?: 'button' | 'submit' | 'reset';
    disabled?: boolean;
    label: string;
    icon?: IconType;
    as?: 'button' | 'link';
    href?: string;
    className?: string;
    showIconRight?: boolean;
    showIconLeft?: boolean;
    iconSize?: number;
    variant?: ButtonVariant;
    title?: string;
    pressAnimation?: boolean;
}

const Button: React.FC<ButtonProps> = ({
    onClick,
    type = 'button',
    disabled = false,
    label,
    icon: Icon,
    as = 'button',
    href,
    className,
    showIconLeft,
    showIconRight = true,
    iconSize = 16,
    variant,
    title,
    pressAnimation = true,
}) => {
    const buttonClasses = [
        'h-[2.5rem] w-max flex items-center justify-center',
        label?.length > 0 ? 'gap-2' : '',
        'rounded-sm px-6',
        pressAnimation && 'enabled:transition-[transform,box-shadow] enabled:duration-200 enabled:ease-[cubic-bezier(0.2,0,0,1)] enabled:active:scale-95',
        variant ? variantClasses[variant] : '',
        className ?? '',
    ].filter(Boolean).join(' ');

    const buttonContent = (
        <>
            {showIconLeft && Icon && <Icon size={iconSize} />}
            <span className='text-llarge text-secondary-large'>{label}</span>
            {showIconRight && Icon && <Icon size={iconSize} className='shrink-0' />}
        </>
    );

    if (as === 'link') {
        return (
            <button
                onClick={onClick}
                className={buttonClasses}
                style={{ textDecoration: 'none', ...(disabled && { opacity: 0.5, cursor: 'not-allowed' }) }}
                disabled={disabled}
            >
                {buttonContent}
            </button>
        );
    }

    return (
        <button
            onClick={onClick}
            type={type}
            disabled={disabled}
            className={`${buttonClasses} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            {...(title ? { title } : {})}
        >
            {buttonContent}
        </button>
    );
};

export default Button;